import JobsService from './jobs-service';
import { JobStatus } from './JobStatus';
import { JobViewModel } from './models';
import { JobActions } from './JobsActions';
import { getDateFilterProps, getTextFilterProps } from './utils';
import {
  modalUtils,
  hasPermissionToRender,
  toSafeArray,
  canAccessQuestProduct,
} from '@/app/core/utils';
import { getCurrentFeatureCode } from '@/app/core/auth';
import { useEffect, useState, Key, useRef, Fragment } from 'react';
import { Table } from 'antd';
import {
  Job,
  JobsSearchResponse,
  JobStatus as JobStatusEnum,
  PaginationConfig,
  SortConfig,
  SortDirection,
  SearchRequest,
  Feature,
  FeatureCodes,
  ProgramPermissions,
  DashboardPermissions,
} from '@ssar-app/domain';
import { TablePaginationConfig } from 'antd/lib/table/Table';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import dayjs, { extend as dayJsExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import relativePlugin from 'dayjs/plugin/relativeTime';
import { useHistory } from 'react-router-dom';
import { usePathContext } from '@ssar-app/okta-authn-react';
import { FaPlusCircle, FaRedo } from 'react-icons/fa/index.js';
import {
  Box,
  Button,
  Flex,
  HStack,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { RadioGroup, getRandomNumbers } from '@ssar-app/uikit';

dayJsExtend(utcPlugin);
dayJsExtend(relativePlugin);

const autoRefreshInterval = 30 * 1000;
const statusFilter: Record<string, string>[] = Object.entries(
  JobStatusEnum,
).map(([text, value]) => ({
  text,
  value,
}));

const fetchJobs = JobsService.getJobs;

interface JobsListProps {
  activeTab: string;
  feature: Feature;
}

export const JobsList = (props: JobsListProps) => {
  const [jobs, setJobs] = useState<JobViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState('all');
  const pagination = useRef<TablePaginationConfig>({
    hideOnSinglePage: false,
    defaultCurrent: 1,
    current: 1,
    total: 0,
  });
  const filtersPassed = useRef<Record<string, (Key | boolean)[] | null>>({});
  const sorterPassed = useRef<
    SorterResult<JobViewModel> | SorterResult<JobViewModel>[]
  >([]);
  const setTimeoutId = useRef(0);

  const canCreate = hasPermissionToRender(
    getCurrentFeatureCode(),
    ProgramPermissions.MayCreatePrograms,
  );
  const history = useHistory();

  const { feature } = props;
  const [statusCount, setStatusCount] = useState({
    completed: 0,
    cancelled: 0,
    failed: 0,
    apsJobs: 0,
    totalJobs: 0,
    nomalJobs: 0,
  });
  // const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  const isAdmin = hasPermissionToRender(
    getCurrentFeatureCode(),
    DashboardPermissions.MayViewAdminColumns,
  );
  const statusFilterTabs = ['all', 'aps', 'normal'];
  const isStatusTab = (): boolean => {
    return statusFilterTabs.includes(tabValue);
  };

  const columns = [
    {
      title: 'Status',
      align: 'center' as const,
      dataIndex: 'status',
      width: 175,
      ...(isStatusTab() ? { filters: statusFilter, sorter: true } : {}),
      render: (_text: string, row: Job) => {
        return (
          <JobStatus
            {...{
              ...row,
              handleUpdate: handleUpdate,
              actions: !canAccessQuestProduct('2025-10-11')
                ? []
                : row.actions && row.actions.length > 0
                ? row.actions
                : [],
            }}
          />
        );
      },
    },
    {
      title: 'Actions',
      align: 'center' as const,
      render: (_text: string, row: Job) => {
        return (
          <JobActions
            {...{
              record: row,
              setIsLoading: setIsLoading,
              forJobs: true,
            }}
          />
        );
      },
    },
    {
      title: 'Program Name',
      dataIndex: 'programName',
      sorter: true,
      key: 'programName',
      ...getTextFilterProps('Program'),
      render: (text: string | undefined, row: Job) => {
        return (
          <>
            {row.isAps && (
              <Tooltip
                label='Automatically Submitted Program'
                placement='top'
                hasArrow
              >
                <Tag
                  bg='primary'
                  color='white'
                  fontSize='xs'
                  borderRadius='base'
                >
                  {row.templateMetaData?.templateId.toUpperCase()}
                </Tag>
              </Tooltip>
            )}{' '}
            {text}
          </>
        );
      },
    },
    isAdmin
      ? {
          title: 'Subscriber Code',
          align: 'center' as const,
          dataIndex: 'subCode',
          sorter: true,
          key: 'subCode',
          ...getTextFilterProps('Subscriber Code'),
        }
      : null,
    /*    isAdmin
      ? {
          title: 'Company ID',
          align: 'center' as const,
          dataIndex: 'clientId',
          sorter: true,
          key: 'clientId',
          ...getTextFilterProps('Company ID'),
        }
      : null, */
    {
      title: 'Submitted Date',
      dataIndex: 'submittedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
    },
    {
      title: 'Completed Date',
      dataIndex: 'completedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string, row: Job) => {
        if (
          (row.completedDate && row.status === JobStatusEnum.CANCELLED) ||
          row.status === JobStatusEnum.COMPLETED
        ) {
          return dayjs(text).format('YYYY-MMM-DD HH:mm:ss  [GMT]ZZ');
        }
        return null;
      },
    },
    {
      title: 'Duration',
      align: 'center' as const,
      sorter: false,
      render: (text: string, row: Job) => {
        if (
          row.status === JobStatusEnum.COMPLETED ||
          row.status === JobStatusEnum.CANCELLED
        ) {
          return `${dayjs(row.completedDate).from(
            dayjs(row.submittedDate),
            true,
          )}`;
        } else {
          return `${dayjs().from(dayjs(row.submittedDate), true)}`;
        }
      },
    },
    isAdmin
      ? {
          title: 'Submitted By',
          align: 'center' as const,
          dataIndex: 'userId',
          sorter: true,
          key: 'userId',
          ...getTextFilterProps('Submitted By'),
        }
      : null,
    {
      title: 'Project Number',
      align: 'center' as const,
      sorter: true,
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      ...getTextFilterProps('Project Number'),
    },
  ].filter((a) => a !== null);

  /*   const resetButtonStates = (): void => {
    setCanArchive(false);
    // setCanDelete(false);
    setCanDownload(false);
  }; */

  /* const onSelectChange = (selectedRowKeys: (string | number)[]): void => {
    // console.log('selectedRowKeys changed: ', selectedRowKeys);
    setSelectedJobs(selectedRowKeys.map((a) => a.toString()));
  }; */
  /* const rowSelection = {
    selectedRowKeys: selectedJobs.map((a) => a as string | number),
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
  }; */

  const fetchData = (
    paginationInput: TablePaginationConfig = pagination.current,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<JobViewModel> | SorterResult<JobViewModel>[] = [],
    _extra?: TableCurrentDataSource<JobViewModel>,
    fromTimeout?: boolean,
  ): void => {
    if (!fromTimeout) {
      clearTimeout(setTimeoutId.current);
      setIsLoading(true);
    }
    // resetButtonStates();
    filtersPassed.current = { tabFilter: [tabValue], ...filters };
    sorterPassed.current = sorter;
    const paginationConfig: PaginationConfig = {
      pageSize: paginationInput?.pageSize || 10,
      current: paginationInput?.current || 1,
    };
    // console.log('paginationConfig: ', paginationConfig);

    const sortConfig: SortConfig[] = [];

    toSafeArray(sorter).forEach((a) => {
      if (a.field) {
        const dir =
          a.order === 'descend'
            ? SortDirection.Descending
            : a.order === 'ascend'
            ? SortDirection.Ascending
            : null;
        if (dir) {
          sortConfig.push({
            field: a.field.toString(),
            direction: dir,
          });
        }
      }
    });

    const searchModel: SearchRequest = {
      paging: paginationConfig,
      sortBy: sortConfig,
      filter: {
        ...filtersPassed.current,
        system: [feature?.code],
      },
    };

    fetchJobs(searchModel)
      .then((jobsResponse: JobsSearchResponse) => {
        const { paging, data, jobStats } = jobsResponse;
        const mergedPagination = {
          ...pagination.current,
          ...paging,
        };
        const {
          completedCount = 0,
          cancelledCount = 0,
          failedCount = 0,
          apsJobs = 0,
          totalJobs = 0,
          normalJobs = 0,
        } = (jobStats || [])[0];
        mergedPagination.current = +mergedPagination.current;
        mergedPagination.pageSize = +mergedPagination.pageSize;

        pagination.current = mergedPagination;
        setJobs(
          (data || []).map((a) => ({
            ...a,
            status: a.status,
            name: a.programName,
          })),
        );
        setStatusCount({
          completed: completedCount,
          cancelled: cancelledCount,
          failed: failedCount,
          apsJobs: apsJobs,
          totalJobs: totalJobs,
          nomalJobs: normalJobs,
        });
        setTimeoutId.current = setTimeout(
          () =>
            fetchData(
              pagination.current,
              filtersPassed.current,
              sorterPassed.current,
              undefined,
              true,
            ),
          autoRefreshInterval,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as any;
      })
      .catch((err) => {
        console.error(err);
        // const messages = err.response?.data?.messages || [err.message];
        modalUtils.showError(err, 'Error while fetching the Programs');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // const fetchDataWrapper = useCallback(() => fetchData(pagination, {}, []), []);

  // useEffect(() => {
  //   fetchDataWrapper();
  // }, [fetchDataWrapper]);

  useEffect(() => {
    props.activeTab === 'jobs'
      ? fetchData(
          pagination.current,
          filtersPassed.current,
          sorterPassed.current,
        )
      : clearTimeout(setTimeoutId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeTab]);

  useEffect(() => {
    return () => {
      // console.log("I'm unmounting");
      clearTimeout(setTimeoutId.current);
    };
  }, []);

  const handleUpdate = (action: string, externalSystemRef: string): void => {
    setIsLoading(true);
    clearTimeout(setTimeoutId.current);
    JobsService.updateJob(action, externalSystemRef)
      .then((res) => {
        if (res.data?.status && res.data?.status.toLowerCase() === 'success') {
          modalUtils.showSuccessMessage(
            res.data?.message || 'State updated successfully.',
          );
          fetchData(
            pagination.current,
            filtersPassed.current,
            sorterPassed.current,
          );
        } else {
          modalUtils.showError(
            res.data?.message || 'Error while performing action.',
          );
        }
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while performing action.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const { buildPath } = usePathContext();

  const getProgramStatOptions = () => {
    const statOptions = [
      {
        label: ` All Programs (${statusCount.totalJobs})`,
        value: 'all',
      },
      {
        label: `Standard Programs (${statusCount.nomalJobs})`,
        value: 'normal',
      },
      {
        label: `Automated Programs (${statusCount.apsJobs})`,
        value: 'aps',
      },
      {
        label: `Completed (${statusCount.completed})`,
        value: 'completed',
      },
      {
        label: `Cancelled (${statusCount.cancelled})`,
        value: 'cancelled',
      },
      {
        label: `Failed (${statusCount.failed})`,
        value: 'failed',
      },
    ];
    if (getCurrentFeatureCode() !== FeatureCodes.AscendQuest) {
      return statOptions.filter((statOption) => statOption.value !== 'aps');
    }
    return statOptions;
  };
  const [key, setKey] = useState(getRandomNumbers(1)[0]);

  return (
    <div>
      {jobs && (pagination.current?.total || 1) > 0 ? (
        <Fragment key={key}>
          <Flex justifyContent='space-between' sx={{ pb: 3 }}>
            <Text className='card-title' fontSize='lg' textColor='black'>
              Jobs Summary
            </Text>
            <HStack>
              <Button
                colorScheme='gray'
                size='sm'
                variant={'outline'}
                onClick={() => {
                  setKey(getRandomNumbers(1)[0]);
                  setTabValue('all');
                  filtersPassed.current = {
                    ...{},
                    tabFilter: ['all'],
                  };
                  fetchData({}, filtersPassed.current, []);
                }}
                leftIcon={<FaRedo />}
              >
                {'Reset & Refresh'}
              </Button>
              <Button
                variant='solid'
                colorScheme='blue'
                isDisabled={!canCreate}
                leftIcon={<FaPlusCircle />}
                onClick={() => history.push(buildPath(`/program/create`))}
                size='sm'
              >
                Create
              </Button>
              {/* <Button
                  type="default"
                  shape="round"
                  disabled={!canDownload}
                  icon={<FaDownload} />}
                >
                  Download
                </Button>
                <Button
                  type="default"
                  shape="round"
                  disabled={!canArchive}
                  icon={<FaFileDownload} />}
                >
                  Archive
                </Button>
                <Button
                  type="default"
                  shape="round"
                  disabled={!(selectedJobs && selectedJobs.length > 0)}
                  onClick={() => showConfirm()}
                  icon={<FaTrash} />}
                >
                  Delete
                </Button>
                */}
            </HStack>
          </Flex>
          <RadioGroup
            defaultValue='all'
            variant='solid'
            optionsType='button'
            onChange={(value) => {
              setTabValue(value);
              filtersPassed.current = {
                ...filtersPassed.current,
                tabFilter: [value],
              };
              fetchData({}, filtersPassed.current, []);
            }}
            radioButtonProps={{
              shadow: 'none',
              borderRadius: 'none',
            }}
            options={getProgramStatOptions()}
            applyBorderRadiusForStartEndButtons={true}
            borderRadiusForStartEndButtons={5}
          />
          <Box mt={4} w={'full'}>
            <Table
              onChange={fetchData}
              showSorterTooltip={false}
              className='table-data-list'
              size='small'
              bordered={true}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              rowKey={(row) => row.id || (row as any)['_id']}
              loading={isLoading}
              // rowSelection={rowSelection}
              dataSource={jobs}
              columns={columns}
              pagination={{
                ...pagination.current,
                // showTotal,
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} items`,
                position: ['bottomLeft'],
                showSizeChanger: true,
                simple: false,
              }}
            >
              {/* <Column dataIndex="status"></Column> */}
            </Table>
          </Box>
        </Fragment>
      ) : (
        <div>No data/permission.</div>
      )}
      {/* {JSON.stringify(
        {
          pagination,
        },
        null,
        2,
      )} */}
    </div>
  );
};
